const getDataLayer = () => window?.dataLayer

const pushToDataLayer = (obj = {}) => {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.log("%c[Data Layer]", "font-weight: bold", obj)
  } else {
    const dataLayer = getDataLayer()

    if (dataLayer) {
      dataLayer.push(obj)
    }
  }
}

export const trackEvent = (event, args = {}) => {
  pushToDataLayer({
    event,
    ...args,
  })
}

// GA4 measurement ID, loaded via the Google tag inside GTM-N59DC6W
export const GA4_MEASUREMENT_ID = "G-XEGWJ1NGRG"

// gtag.js (loaded by GTM) only processes real `arguments` objects pushed to
// the dataLayer, so this must be a regular function rather than an arrow fn
function gtag() {
  // eslint-disable-next-line prefer-rest-params
  // biome-ignore lint/complexity/noArguments: gtag.js requires a real arguments object
  pushToDataLayer(arguments)
}

/**
 * Send a GA4 page_view for a client-side (SPA) route change.
 *
 * GTM re-fires the Google tag on the plugin's `routeChange` event, but a
 * Google tag only initializes once per page load, so no page_view is sent
 * for Gatsby <Link> navigations. Send it explicitly instead. Callers must
 * skip the initial load, which the Google tag already tracks.
 */
export const trackPageView = (location, prevLocation) => {
  const { origin } = window.location
  const pagePath = `${location.pathname}${location.search}`

  gtag("event", "page_view", {
    send_to: GA4_MEASUREMENT_ID,
    page_location: `${origin}${pagePath}${location.hash}`,
    page_path: pagePath,
    page_title: document.title,
    page_referrer: prevLocation
      ? `${origin}${prevLocation.pathname}${prevLocation.search}`
      : undefined,
  })
}

/**
 * Sends a Segment "Form Submitted" track event (and identify when an email is
 * available) so form fills reach the warehouse alongside the GTM events.
 */
export const trackSegmentFormSubmission = ({
  form_id,
  form_name,
  form_type,
  email,
  ...extra
} = {}) => {
  if (typeof window === "undefined" || !window.analytics) return

  try {
    if (email) {
      window.analytics.identify(email)
    }
    window.analytics.track("Form Submitted", {
      form_id,
      form_name,
      form_type,
      source_page: window.location.pathname,
      source_url: window.location.href,
      ...extra,
    })
  } catch {
    // Analytics must never break form submission
  }
}

/**
 * Sends a Segment "Form Viewed" track event when a form or form modal is
 * shown to the user.
 */
export const trackSegmentFormOpened = ({
  form_id,
  form_name,
  form_type,
  ...extra
} = {}) => {
  if (typeof window === "undefined" || !window.analytics) return

  try {
    window.analytics.track("Form Viewed", {
      form_id,
      form_name,
      form_type,
      source_page: window.location.pathname,
      source_url: window.location.href,
      ...extra,
    })
  } catch {
    // Analytics must never break the UI
  }
}
